import React from 'react';
import {StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import {useResolvedPricing} from '../../../hooks/pricing';
import {AppRoute} from '../../../models/AppRoute';
import useAppNavigation from '../../../navigation/useAppNavigation';
import {renderSticker} from '../../../utils/functions';
import {buildProductTestId} from '../../../utils/string';
import MembersPriceLabel from '../primitives/MembersPriceLabel';
import ProductTileActions from '../primitives/ProductTileActions';
import ProductTileContainer from '../primitives/ProductTileContainer';
import ProductTileImage from '../primitives/ProductTileImage';
import ProductTileInfo from '../primitives/ProductTileInfo';
import ProductTilePricing from '../primitives/ProductTilePricing';
import {TILE_DIMENSIONS} from '../styles';
import {ProductTileVariantProps} from '../types';

/**
 * Compact vertical product tile for grids and carousels.
 * Shows image, name, byline, pricing, and cart button in a vertical layout.
 * Tapping navigates to Product Detail Screen by default.
 */
const ProductTileCompact = observer(function ProductTileCompact({
  item,
  onPress,
  testContext,
  disabled = false,
  isProcessing = false,
  containerStyle,
}: ProductTileVariantProps) {
  const navigation = useAppNavigation();
  const testID = buildProductTestId(item.name, testContext);
  const pricing = useResolvedPricing(item, 'marketing');
  const hasStickers = pricing.visibleStickers.length > 0;

  const handlePress = () => {
    if (isProcessing) return;
    if (onPress) {
      onPress(item);
    } else {
      // Navigate to Product Detail Screen by default
      // @ts-expect-error - Navigation type is complex with nested screens
      navigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {
          screen: AppRoute.PRODUCT,
          params: {product: item},
        },
      });
    }
  };

  return (
    <ProductTileContainer
      item={item}
      onPress={handlePress}
      disabled={disabled || isProcessing}
      containerStyle={[styles.container, containerStyle]}
      testID={testID}>
      {/* MembersPriceLabel at the top of tile, above image */}
      <MembersPriceLabel
        showLabel={pricing.advertiseMemberPrice}
        labelText={pricing.memberLabelText}
        center
      />

      <View style={styles.imageContainer}>
        <ProductTileImage item={item} size="medium" testID={`${testID}-image`} />
        {/* Stickers positioned relative to image area */}
        {hasStickers ? (
          <View style={styles.stickerContainer}>
            {pricing.visibleStickers.map(sticker => renderSticker(sticker))}
          </View>
        ) : null}
      </View>

      <View style={styles.contentContainer}>
        {/* Top section: Info and Pricing grouped together */}
        <View>
          <ProductTileInfo
            item={item}
            nameNumberOfLines={2}
            testID={`${testID}-name`}
            containerStyle={styles.infoContainer}
          />

          <ProductTilePricing
            item={item}
            showMemberLabel={false}
            showOriginalPrice
            testID={`${testID}-price`}
            containerStyle={styles.pricingContainer}
          />
        </View>

        {/* Bottom section: Actions pinned to bottom */}
        <ProductTileActions
          item={item}
          testContext={testContext}
          testID={`${testID}-actions`}
          containerStyle={styles.actionsContainer}
        />
      </View>
    </ProductTileContainer>
  );
});

export default ProductTileCompact;

const styles = StyleSheet.create({
  container: {
    width: TILE_DIMENSIONS.width,
    padding: TILE_DIMENSIONS.containerPadding,
  },
  imageContainer: {
    position: 'relative',
    alignItems: 'center',
    marginTop: TILE_DIMENSIONS.imageMarginTop,
    marginBottom: TILE_DIMENSIONS.imageMarginBottom,
  },
  stickerContainer: {
    position: 'absolute',
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    pointerEvents: 'none',
  },
  contentContainer: {
    flex: 1,
    justifyContent: 'space-between',
  },
  infoContainer: {
    marginBottom: TILE_DIMENSIONS.infoMarginBottom,
  },
  pricingContainer: {
    marginBottom: TILE_DIMENSIONS.pricingMarginBottom,
  },
  actionsContainer: {
    width: '100%',
    alignItems: 'stretch',
  },
});
